import { renderToString } from 'react-dom/server';
import { writeFileSync, mkdirSync, existsSync } from 'fs';
import { join, dirname } from 'path';
import { Route, SEOConfig } from '../../types';
import React from 'react';

export interface SSGContext {
    route: Route;
    params: { [key: string]: string };
    searchParams: { [key: string]: string | string[] | undefined };
    seoConfig?: SEOConfig;
}

export async function buildStaticPages(
    routes: Route[],
    App: React.ComponentType<any>,
    outDir: string,
    getStaticProps?: (route: Route) => Promise<{ [key: string]: any }[]>
): Promise<void> {
    for (const route of routes) {
        if (route.isDynamic && getStaticProps) {
            // Generar páginas dinámicas
            const staticProps = await getStaticProps(route);

            for (const props of staticProps) {
                const params = extractParamsFromProps(route, props);
                await generateStaticPage(route, App, outDir, { params });
            }
        } else {
            // Generar página estática
            await generateStaticPage(route, App, outDir);
        }
    }

    // Generar sitemap.xml
    await generateSitemap(routes, outDir);
}

async function generateStaticPage(
    route: Route,
    App: React.ComponentType<any>,
    outDir: string,
    context?: { params: { [key: string]: string } }
): Promise<void> {
    const html = await renderPageToString(route, App, context);
    const outputPath = getOutputPath(route, outDir, context?.params);

    // Crear directorio si no existe
    const dir = dirname(outputPath);
    if (!existsSync(dir)) {
        mkdirSync(dir, { recursive: true });
    }

    writeFileSync(outputPath, html);
}

async function renderPageToString(
    _route: Route,
    App: React.ComponentType<any>,
    context?: { params: { [key: string]: string } }
): Promise<string> {
    const props = {
        params: context?.params || {},
        searchParams: {}
    };

    const content = renderToString(React.createElement(App, props));

    return generateHTMLTemplate(content, undefined, ['/assets/index.js']);
}

function getOutputPath(
    route: Route,
    outDir: string,
    params?: { [key: string]: string }
): string {
    let path = route.path;

    if (params) {
        // Reemplazar parámetros dinámicos con valores reales
        for (const [key, value] of Object.entries(params)) {
            path = path.replace(`[${key}]`, value);
        }
    }

    if (path === '/') {
        return join(outDir, 'index.html');
    }

    return join(outDir, path, 'index.html');
}

function extractParamsFromProps(route: Route, props: { [key: string]: any }): { [key: string]: string } {
    const params: { [key: string]: string } = {};

    for (const param of route.params || []) {
        if (props[param]) {
            params[param] = String(props[param]);
        }
    }

    return params;
}

async function generateSitemap(routes: Route[], outDir: string): Promise<void> {
    const sitemap = `<?xml version="1.0" encoding="UTF-8"?>
<urlset xmlns="http://www.sitemaps.org/schemas/sitemap/0.9">
${routes
            .filter(route => !route.isDynamic)
            .map(route => `  <url>
    <loc>https://example.com${route.path}</loc>
    <changefreq>weekly</changefreq>
    <priority>0.8</priority>
  </url>`)
            .join('\n')}
</urlset>`;

    writeFileSync(join(outDir, 'sitemap.xml'), sitemap);
}

function generateHTMLTemplate(
    content: string,
    seoConfig?: SEOConfig,
    scripts?: string[]
): string {
    const metaTags = seoConfig ? generateMetaTags(seoConfig) : '';
    const scriptTags = scripts ? scripts.map(src => `<script src="${src}"></script>`).join('\n') : '';

    return `<!DOCTYPE html>
<html lang="es">
<head>
  <meta charset="UTF-8">
  <meta name="viewport" content="width=device-width, initial-scale=1.0">
  ${metaTags}
</head>
<body>
  <div id="root">${content}</div>
  ${scriptTags}
</body>
</html>`;
}

function generateMetaTags(config: SEOConfig): string {
    const tags: string[] = [];

    if (config.title) {
        tags.push(`<title>${escapeHtml(config.title)}</title>`);
    }

    if (config.description) {
        tags.push(`<meta name="description" content="${escapeHtml(config.description)}">`);
    }

    if (config.ogTitle || config.title) {
        tags.push(`<meta property="og:title" content="${escapeHtml(config.ogTitle || config.title!)}">`);
    }

    if (config.ogDescription || config.description) {
        tags.push(`<meta property="og:description" content="${escapeHtml(config.ogDescription || config.description!)}">`);
    }

    if (config.ogImage) {
        tags.push(`<meta property="og:image" content="${escapeHtml(config.ogImage)}">`);
    }

    const twitterCard = config.twitterCard || 'summary_large_image';
    tags.push(`<meta name="twitter:card" content="${escapeHtml(twitterCard)}">`);

    if (config.ogTitle || config.title) {
        tags.push(`<meta name="twitter:title" content="${escapeHtml(config.ogTitle || config.title!)}">`);
    }

    if (config.ogDescription || config.description) {
        tags.push(`<meta name="twitter:description" content="${escapeHtml(config.ogDescription || config.description!)}">`);
    }

    if (config.ogImage) {
        tags.push(`<meta name="twitter:image" content="${escapeHtml(config.ogImage)}">`);
    }

    if (config.keywords && config.keywords.length > 0) {
        tags.push(`<meta name="keywords" content="${escapeHtml(config.keywords.join(', '))}">`);
    }

    if (config.canonical) {
        tags.push(`<link rel="canonical" href="${escapeHtml(config.canonical)}">`);
    }

    return tags.join('\n');
}

function escapeHtml(text: string): string {
    const map: { [key: string]: string } = {
        '&': '&amp;',
        '<': '&lt;',
        '>': '&gt;',
        '"': '&quot;',
        "'": '&#039;'
    };

    return text.replace(/[&<>"']/g, (m) => map[m]);
}
